import React from 'react';
import { RotateCcw, StickyNote, Edit3 } from 'lucide-react';
import { isBusinessDay } from '../../utils';

export function MonthCalendar({ monthName, mIdx, year, stats, t, language, getDayData, toggleDay, openNoteModal, setHoverNote, onResetMonth }) {
  const firstDay = new Date(year, mIdx, 1).getDay();
  const daysInMonth = new Date(year, mIdx + 1, 0).getDate();

  return (
    <div className="border border-gray-100 rounded-xl p-3 sm:p-4 bg-gray-50/30">
      <div className="flex justify-between items-center mb-1">
        <h3 className="font-bold text-gray-800 text-sm">{monthName}</h3>
        <button
          onClick={onResetMonth}
          title={t.practice.resetMonth}
          data-testid={`practice-month-reset-${mIdx}`}
          className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-all"
        >
          <RotateCcw size={12} />
        </button>
      </div>
      <div className="flex justify-between mb-2 sm:mb-3">
        <div className="text-[9px] sm:text-[10px] flex gap-1 sm:gap-2 flex-wrap">
          <span className="text-gray-600 font-bold">T: {stats.total}</span>
          <span className="text-emerald-600 font-bold">P: {stats.positive}</span>
          <span className="text-red-600 font-bold">N: {stats.negative}</span>
          <span className="text-indigo-600 font-bold">{stats.winRate.toFixed(0)}%</span>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-0.5 sm:gap-1 text-center mb-1 sm:mb-2">
        {t.practice.daysShort.map((d, i) => (
          <div key={`${d}-${i}`} className="text-[8px] sm:text-[10px] font-bold text-gray-400">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const date = new Date(year, mIdx, day);
          const isAllowed = isBusinessDay(date);
          const key = `${year}-${mIdx + 1}-${day}`;
          const dayData = getDayData(key);
          const val = dayData.status;
          const hasNote = dayData.note && dayData.note.trim();

          return (
            <div
              key={day}
              className="relative group"
              onMouseEnter={() => hasNote && setHoverNote({ dateKey: key, note: dayData.note })}
              onMouseLeave={() => setHoverNote(null)}
            >
              <button
                disabled={!isAllowed}
                onClick={() => toggleDay(key, isAllowed)}
                data-testid={`practice-day-${key}`}
                className={`
                  h-5 w-5 sm:h-7 sm:w-7 rounded-md sm:rounded-lg text-[8px] sm:text-[10px] font-bold flex items-center justify-center transition-all relative
                  ${!isAllowed ? 'bg-gray-100 text-gray-300 cursor-not-allowed' :
                    val === 1 ? 'bg-emerald-500 text-white shadow-sm' :
                    val === 0 ? 'bg-red-500 text-white shadow-sm' :
                    'bg-white border border-gray-200 text-gray-600 hover:border-indigo-400'}
                `}
              >
                {day}
                {hasNote && (
                  <StickyNote
                    size={8}
                    className="absolute -top-0.5 -right-0.5 text-yellow-400 fill-yellow-400 drop-shadow-sm"
                  />
                )}
              </button>
              {isAllowed && (
                <button
                  onClick={(e) => openNoteModal(key, e)}
                  data-testid={`practice-note-btn-${key}`}
                  className="absolute -bottom-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-blue-500 text-white rounded-full p-0.5 hover:bg-blue-600 shadow-md"
                  title={language === 'es' ? 'Agregar/editar nota' : 'Add/edit note'}
                >
                  <Edit3 size={8} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
